/**
 * Component displaying a single change record with before/after comparison.
 */

import { Tag, Descriptions, Typography } from 'antd';
import type { RequirementChangeRecord } from '@/types';
import dayjs from 'dayjs';
import { useT } from '@/i18n';

const { Text } = Typography;

interface ChangeRecordItemProps {
  record: RequirementChangeRecord;
}

// Map field names to Chinese labels
const FIELD_LABELS: Record<string, string> = {
  reqId: '需求ID',
  owner: '责任人',
  description: '需求描述',
  deliveryStage: '交付阶段',
  attachment: '设计文档',
  priority: '优先级',
  deliverables: '交付物',
  verificationMethod: '验证方法',
  remark: '备注',
  status: '需求状态',
  linkedPackage: '需求交付首版软件',  // BSP 本地数据，显示当前关联
};

// Change type color mapping
const CHANGE_TYPE_COLORS: Record<string, string> = {
  initial: 'default',
  created: 'success',
  updated: 'processing',
  deleted: 'error',
  synced_add: 'cyan',
  synced_delete: 'orange',
  synced_replace: 'blue',
};

// Change type labels
const CHANGE_TYPE_LABELS: Record<string, string> = {
  initial: '初始版本',
  created: '新增需求',
  updated: '修改需求',
  deleted: '删除需求',
  synced_add: '同步添加',
  synced_delete: '同步删除',
  synced_replace: '同步修改',
};

const ChangeRecordItem: React.FC<ChangeRecordItemProps> = ({ record }) => {
  const { t } = useT();
  const { requirement_id, change_type, changed_fields, change_summary, changed_at } = record;

  const hasFieldChanges = changed_fields && Object.keys(changed_fields).length > 0;

  return (
    <div style={{ padding: '10px 16px', border: '1px solid #f0f0f0', borderRadius: 4, marginBottom: 8 }}>
      {/* Header with requirement ID, change type, and timestamp */}
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text strong>{requirement_id}</Text>
          <Tag color={CHANGE_TYPE_COLORS[change_type]} style={{ marginLeft: 8 }}>
            {t(CHANGE_TYPE_LABELS[change_type] || change_type)}
          </Tag>
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(changed_at).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      </div>

      {/* Change summary */}
      {change_summary && (
        <div style={{ marginBottom: hasFieldChanges ? 12 : 0 }}>
          <Text type="secondary">{change_summary}</Text>
        </div>
      )}

      {/* Field-level changes (for update type) */}
      {hasFieldChanges && (
        <Descriptions
          column={1}
          size="small"
          bordered
          styles={{
            label: { width: 180, fontWeight: 500 },
            content: { wordBreak: 'break-word', overflowWrap: 'break-word' }
          }}
        >
          {Object.entries(changed_fields).map(([fieldName, change]) => {
            const fieldLabel = t(FIELD_LABELS[fieldName] || fieldName);
            return (
              <Descriptions.Item key={fieldName} label={fieldLabel}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                  {change.before !== null && change.before !== undefined && (
                    <span style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('变更前:')} </Text>
                      <Text delete style={{ color: '#ff4d4f' }}>{change.before || t('(空)')}</Text>
                    </span>
                  )}
                  {change.before !== null && change.before !== undefined &&
                   change.after !== null && change.after !== undefined && (
                    <Text type="secondary">→</Text>
                  )}
                  {change.after !== null && change.after !== undefined && (
                    <span style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('变更后:')} </Text>
                      <Text strong style={{ color: '#52c41a' }}>{change.after || t('(空)')}</Text>
                    </span>
                  )}
                </div>
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      )}
    </div>
  );
};

export default ChangeRecordItem;
